import { textColorFields } from '../_shared/textColor.js';
import { animationFields, mediaRadiusField } from '../_shared/universal.js';
import { headingHighlightFields } from '../_shared/headingHighlight.js';
import { patternFields } from '../_shared/pattern.js';
/**
 * insta-stories — a FROM-SCRATCH, 1:1 rebuild of Shrine Pro's "Instagram stories" section
 * (shrine-pro-ref/sections/insta-stories.liquid). Story-opener circles (gradient ring + profile picture)
 * open a full-screen, Instagram-style modal that auto-plays each user's up-to-3 images/videos with
 * segmented progress bars, prev/next + swipe navigation, pause/resume and mute — driven React-free by
 * {@link ../insta-stories/instaStoriesInit}.
 *
 * Field-type mapping (Shopify → ours): inline_richtext→text (with bold/italic highlight markup),
 * image_picker→image, video→video, color→color, color_background→gradient, header→group, `visible_if`→our
 * `visibleWhen`. Ids are clean camelCase; labels/options/defaults stay faithful to Shrine. The word-
 * HIGHLIGHT engine (8 styles on bold/italic words) and the 13-pattern BACKGROUND engine are the shared
 * factories every other rebuilt section uses, so this inspector matches them exactly.
 */
/** Convenience for the literal hex defaults Shrine ships. */
const hex = (value) => ({ ref: 'custom', value });
const GENERAL = 'General';
/** The three per-user story slots (#1/#2/#3) — image OR video, media fit, time posted, contain background. */
function storyFields(n) {
    const group = `Story #${n}`;
    const defaultTime = n === 1 ? '10m' : n === 2 ? '1h' : '4h';
    return [
        { id: `story${n}Image`, type: 'image', label: `Story #${n} image`, group },
        { id: `story${n}Video`, type: 'video', label: `Story #${n} video`, group },
        {
            id: `story${n}MediaFit`,
            type: 'select',
            label: `Story #${n} media fit`,
            group,
            default: 'contain',
            info: "Contain preserves the media's aspect ratio, cover fills out the whole story box.",
            options: [
                { value: 'contain', label: 'Contain' },
                { value: 'cover', label: 'Cover' },
            ],
        },
        {
            id: `story${n}TimePosted`,
            type: 'text',
            label: `Story #${n} time posted`,
            group,
            default: defaultTime,
        },
        {
            id: `story${n}Bg`,
            type: 'color',
            label: `Story #${n} background`,
            group,
            default: hex('#282828'),
            info: 'Visible if media fit is set to Contain.',
        },
    ];
}
export const instaStoriesSchema = {
    type: 'insta-stories',
    name: 'Instagram stories',
    version: 1,
    templates: ['index', 'collection', 'page', 'thank-you'],
    // Fixed chrome glyphs this section always renders (opener/nav icons). Declared so the storefront's
    // Material Symbols `icon_names=` subset ships them — they come from markup, not an `icon-name` field.
    staticIcons: [
        'person',
        'verified',
        'pause',
        'play_arrow',
        'close',
        'chevron_left',
        'chevron_right',
    ],
    fields: [
        // ── (top, ungrouped) ──────────────────────────────────────────────────────
        {
            id: 'displaySectionId',
            type: 'checkbox',
            label: 'Display section ID',
            default: false,
            info: 'Lets a button link scroll to this section.',
        },
        {
            id: 'visibility',
            type: 'select',
            label: 'Display on',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'mobile-hidden', label: 'Desktop only' },
                { value: 'desktop-hidden', label: 'Mobile only' },
            ],
        },
        // ── General: heading + highlight + size ─────────────────────────────────────
        {
            id: 'heading',
            type: 'text',
            label: 'Heading',
            group: GENERAL,
            default: 'Instagram stories',
            defaultKey: 'section.insta_stories',
            info: 'Wrap words in ** ** for highlight 1, * * for highlight 2.',
        },
        ...headingHighlightFields(1, GENERAL),
        ...headingHighlightFields(2, GENERAL),
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: GENERAL,
            default: 'h1',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'headingSizeDesktop',
            type: 'range',
            label: 'Custom desktop size',
            group: GENERAL,
            min: 24,
            max: 60,
            step: 3,
            unit: 'px',
            default: 39,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'headingSizeMobile',
            type: 'range',
            label: 'Custom mobile size',
            group: GENERAL,
            min: 20,
            max: 50,
            step: 2,
            unit: 'px',
            default: 28,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'text',
            type: 'text',
            label: 'Text',
            group: GENERAL,
            default: 'Display images and testimonials in a format familiar to your customers.',
            defaultKey: 'section.insta_stories_text',
        },
        // ── General: color scheme + custom palette ──────────────────────────────────
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: GENERAL,
            default: 'background-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customBackground',
            type: 'color',
            label: 'Custom background color',
            group: GENERAL,
            default: hex('#FFFFFF'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customGradientBackground',
            type: 'gradient',
            label: 'Custom gradient background',
            group: GENERAL,
            info: 'If set, replaces the background color.',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customImageBackground',
            type: 'image',
            label: 'Custom image background',
            group: GENERAL,
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customMobileImageBackground',
            type: 'image',
            label: 'Custom mobile image background',
            group: GENERAL,
            info: 'If empty, the desktop image is used on mobile too.',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customText',
            type: 'color',
            label: 'Custom text color',
            group: GENERAL,
            default: hex('#121212'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        // ── General: background pattern (shared engine) ─────────────────────────────
        ...patternFields(GENERAL),
        // ── Stories: autoplay speed ─────────────────────────────────────────────────
        {
            id: 'autoplaySpeed',
            type: 'range',
            label: 'Image autoplay speed',
            group: 'Stories',
            min: 5,
            max: 15,
            step: 1,
            unit: 'sec',
            default: 10,
            info: 'On touch devices, the opener arrows become swipe/drag and stories can be swiped.',
        },
        mediaRadiusField,
        // ── Section padding ─────────────────────────────────────────────────────────
        {
            id: 'paddingTop',
            type: 'range',
            label: 'Top padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        {
            id: 'paddingBottom',
            type: 'range',
            label: 'Bottom padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // The universal Animation group (2026-09-18): this section writes its own section controls
        // instead of spreading `universalFields`, so the group is spread in on its own.
        ...animationFields,
    ],
    // Inline blocks — one "User post" type (Shrine's `user` block): the profile + up to 3 stories.
    blocks: [
        {
            type: 'user',
            name: 'User post',
            fields: [
                { id: 'username', type: 'text', label: 'User name', default: 'shrinetheme' },
                { id: 'verified', type: 'checkbox', label: 'Verified account', default: false },
                { id: 'profilePicture', type: 'image', label: 'Profile picture' },
                { id: 'closeFriends', type: 'checkbox', label: 'Close friends story', default: false },
                ...storyFields(1),
                ...storyFields(2),
                ...storyFields(3),
            ],
        },
    ],
};
export const instaStoriesPresets = [
    {
        name: 'Instagram stories',
        settings: {
            headingSize: 'h1',
            colorScheme: 'background-1',
            autoplaySpeed: 10,
            paddingTop: 36,
            paddingBottom: 36,
        },
        blocks: [{ type: 'user' }, { type: 'user' }, { type: 'user' }],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const instaStoriesMeta = {
    label: 'Instagram stories',
    group: 'Content',
    icon: 'grid',
};
//# sourceMappingURL=schema.js.map